import type { SpiffProcessInstanceRow } from "@/api/dto/process-instance";

import type { CaseStatus } from "./case";
import type { CaseConfig } from "./case-config";
import { metadataOf } from "./metadata";

/**
 * 生ステータス + metadata + 現在タスクから UI 上のステータスを導出する。
 *
 * 判定順（上から最初に一致したものを採用）:
 *  1. error / 2. terminated / 3. suspended … 終了状態は他より優先
 *  4. complete かつ差し戻し metadata あり → returned
 *  5. complete → completed
 *  6. 非終了かつ起案タスクが開いている → 差し戻し metadata があれば returned、無ければ draft
 *  7. それ以外 → inProgress
 *
 * 差し戻しの表れ方はモデルの作りで 2 通りあり、どちらも見る必要がある。
 *  - 起案に戻す（デモ 10 フロー・sales-forecast 通常版）… 案件は終わらず起案タスクが
 *    再び開く。下書きと同じ形になるので判定 metadata で区別する
 *  - 差し戻しで終了する（sales-forecast 部署スコープ版）… complete になる
 *
 * `last_milestone_bpmn_name` は使えない（対象モデルに milestone 要素が無い）。
 * → いずれも metadata が唯一の実用手段。
 */
export function deriveCaseStatus(
  row: SpiffProcessInstanceRow,
  config: CaseConfig,
): CaseStatus {
  if (row.status === "error") return "error";
  if (row.status === "terminated") return "canceled";
  if (row.status === "suspended") return "suspended";

  if (row.status === "complete") {
    return isRejected(row, config) ? "returned" : "completed";
  }

  if (isDraft(row, config)) {
    // 起案に戻された案件も起案タスクが開く。区別は判定 metadata でしかできない
    return isRejected(row, config) ? "returned" : "draft";
  }

  return "inProgress";
}

function isRejected(row: SpiffProcessInstanceRow, config: CaseConfig): boolean {
  return metadataOf(row, config.rejectionMetadataKey) === config.rejectionValue;
}

function isDraft(row: SpiffProcessInstanceRow, config: CaseConfig): boolean {
  const taskName = row.task_name;
  if (taskName === null || taskName === undefined) return false;

  return config.draftTaskNames.includes(taskName);
}
